import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import {
  ScreenHeader, Surface, Button, Badge, Divider, Avatar,
  Icon, FeedbackMsg, inputCss, AlunoSearch, FormField,
} from '../components/ui';

const API_URL = process.env.REACT_APP_API_URL;

const MESES_FULL  = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

const selectStyle = {
  height: 40, padding: '0 12px', fontSize: 14, width: '100%',
  background: 'var(--bg-sunken)', color: 'var(--text-primary)',
  border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)',
  fontFamily: 'inherit', outline: 'none',
};

// input[type=date] no iOS ignora width:100% — desabilita aparência nativa p/ caber no card
const dateInputStyle = {
  ...inputCss, display: 'block', width: '100%', maxWidth: '100%', minWidth: 0,
  boxSizing: 'border-box', WebkitAppearance: 'none', MozAppearance: 'none', appearance: 'none',
  // sem aparência nativa o texto sobe; line-height = altura interna (40 - 2px borda) centraliza
  lineHeight: '38px',
};

function fmtMoney(v) {
  return `R$ ${Number(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtHoras(min) {
  const h = Math.floor(min / 60), m = min % 60;
  if (h === 0) return `${m}min`;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}
function fmtDur(min) {
  const h = Math.floor(min / 60), m = min % 60;
  return h > 0 ? (m > 0 ? `${h}h${m}m` : `${h}h`) : `${m}min`;
}

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      flex: 1, display: 'flex', flexDirection: 'column', gap: 2,
      padding: '12px 14px',
      borderRadius: 'var(--radius-md)',
      background: accent ? 'var(--accent-soft)' : 'var(--bg-sunken)',
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4,
        color: accent ? 'var(--accent)' : 'var(--text-tertiary)' }}>{label}</div>
      <div style={{ fontSize: 19, fontWeight: 700, letterSpacing: -0.5,
        fontFamily: '"Geist Mono", monospace', color: accent ? 'var(--accent)' : 'var(--text-primary)',
        fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{sub}</div>}
    </div>
  );
}

function ModalidadeCard({ label, icon, data, colorBg, colorFg }) {
  if (!data || data.aulas === 0) return null;
  return (
    <div style={{
      flex: 1, borderRadius: 'var(--radius-md)', padding: '12px 14px',
      background: colorBg, border: `1px solid ${colorFg}22`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Icon name={icon} size={13} style={{ color: colorFg }}/>
        <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, color: colorFg }}>{label}</span>
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.5,
        fontFamily: '"Geist Mono", monospace', color: colorFg }}>
        {fmtMoney(data.valor_total)}
      </div>
      <div style={{ fontSize: 11, color: colorFg, opacity: 0.75, marginTop: 3 }}>
        {data.aulas} aula{data.aulas !== 1 ? 's' : ''} · {fmtHoras(data.minutos)}
        {data.valor_hora > 0 && ` · ${fmtMoney(data.valor_hora)}/h`}
      </div>
    </div>
  );
}

const pad2 = n => String(n).padStart(2, '0');
const toYMD = d => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

export default function Relatorio() {
  const { tipo, usuarioId } = useAuth();
  const hoje = new Date();
  const [alunos,     setAlunos]     = useState([]);
  const [alunoId,    setAlunoId]    = useState(tipo === 'aluno' ? (usuarioId || '') : '');
  const [modo,       setModo]       = useState('mes_atual');
  const [mes,        setMes]        = useState(pad2(hoje.getMonth() + 1));
  const [ano,        setAno]        = useState(hoje.getFullYear());
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim,    setDataFim]    = useState('');
  const [relatorio,  setRelatorio]  = useState(null);
  const [msg,        setMsg]        = useState('');
  const [loading,    setLoading]    = useState(false);

  useEffect(() => {
    if (tipo === 'professora')
      axios.get(`${API_URL}/alunos_aprovados`, { withCredentials: true })
        .then(r => setAlunos(r.data)).catch(() => {});
  }, [tipo]);

  // Converte o modo selecionado em datas de início e fim (YYYY-MM-DD)
  function resolverPeriodo() {
    const now = new Date();
    if (modo === 'mes_atual') {
      return { inicio: toYMD(new Date(now.getFullYear(), now.getMonth(), 1)),
               fim:    toYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
    }
    if (modo === 'mes_passado') {
      return { inicio: toYMD(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
               fim:    toYMD(new Date(now.getFullYear(), now.getMonth(), 0)) };
    }
    if (modo === 'especifico') {
      if (!mes) return null;
      const m = parseInt(mes, 10);
      return { inicio: toYMD(new Date(ano, m - 1, 1)),
               fim:    toYMD(new Date(ano, m, 0)) };
    }
    // personalizado
    if (!dataInicio || !dataFim) return null;
    return { inicio: dataInicio, fim: dataFim };
  }

  function buscar() {
    setMsg(''); setRelatorio(null);
    if (!alunoId) { setMsg('Selecione o aluno.'); return; }
    const periodo = resolverPeriodo();
    if (!periodo) {
      setMsg(modo === 'personalizado' ? 'Informe as datas de início e fim.' : 'Selecione o mês.');
      return;
    }
    if (periodo.inicio > periodo.fim) { setMsg('A data de início não pode ser posterior à data de fim.'); return; }
    setLoading(true);
    axios.get(`${API_URL}/relatorio_periodo/${alunoId}`, { params: periodo, withCredentials: true })
      .then(r => setRelatorio(r.data))
      .catch(err => setMsg(err.response?.data?.erro || 'Erro ao buscar relatório.'))
      .finally(() => setLoading(false));
  }

  function exportarCSV() {
    if (!relatorio) return;
    const r = relatorio;
    let csv = `Aluno;Período;Aulas;Horas;Total (R$)\n`;
    csv += `${r.aluno};${r.periodo};${r.resumo.aulas};${r.resumo.horas};${r.resumo.valor_total}\n\n`;
    csv += `Data;Hora;Duração (min);Tipo;Modalidade;Valor/h (R$);Valor Aula (R$);Status\n`;
    r.agendamentos.forEach(a => {
      csv += `${a.data};${a.hora_inicio};${a.duracao};${a.tipo};${a.modalidade};${a.valor_hora};${a.valor_aula};${a.status}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const el   = document.createElement('a');
    el.href = url; el.download = `relatorio_${r.aluno}_${r.periodo.replace(/[/ ]/g, '-')}.csv`;
    el.click(); URL.revokeObjectURL(url);
  }

  async function exportarPDF() {
    if (!relatorio) return;
    const { default: jsPDF }    = await import('jspdf');
    const { default: autoTable } = await import('jspdf-autotable');
    const r   = relatorio;
    const doc = new jsPDF();
    const purple = [58, 24, 84];
    const teal   = [62, 184, 177];

    // Header
    doc.setFillColor(...purple);
    doc.rect(0, 0, 210, 36, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18); doc.setTextColor('#fff');
    doc.text('Relatório Mensal', 14, 20);
    doc.setFontSize(10); doc.setFont('helvetica', 'normal');
    doc.text(`${r.aluno}  ·  ${r.periodo}`, 14, 29);

    // Summary row
    const sumY = 44;
    const boxes = [
      { label: 'Aulas',    value: String(r.resumo.aulas) },
      { label: 'Horas',    value: `${r.resumo.horas}h` },
      { label: 'Total',    value: `R$ ${r.resumo.valor_total.toFixed(2)}` },
    ];
    boxes.forEach((b, i) => {
      const x = 14 + i * 62;
      doc.setFillColor(245, 240, 250);
      doc.roundedRect(x, sumY, 58, 22, 3, 3, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(14); doc.setTextColor(...purple);
      doc.text(b.value, x + 29, sumY + 13, { align: 'center' });
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(100, 100, 100);
      doc.text(b.label, x + 29, sumY + 20, { align: 'center' });
    });

    // Breakdown per modalidade
    let nextY = sumY + 30;
    if (r.particular.aulas > 0 || r.grupo.aulas > 0) {
      const cols = [];
      if (r.particular.aulas > 0) cols.push(['Particular', r.particular.aulas, fmtHoras(r.particular.minutos), `R$ ${r.particular.valor_hora.toFixed(2)}/h`, `R$ ${r.particular.valor_total.toFixed(2)}`]);
      if (r.grupo.aulas > 0)      cols.push(['Grupo',      r.grupo.aulas,      fmtHoras(r.grupo.minutos),      `R$ ${r.grupo.valor_hora.toFixed(2)}/h`,      `R$ ${r.grupo.valor_total.toFixed(2)}`]);
      autoTable(doc, {
        startY: nextY,
        head: [['Modalidade', 'Aulas', 'Horas', 'Valor/h', 'Subtotal']],
        body: cols,
        theme: 'grid',
        styles: { halign: 'center', fontSize: 10, cellPadding: 5 },
        headStyles: { fillColor: teal, textColor: '#fff', fontStyle: 'bold' },
        margin: { left: 14, right: 14 },
      });
      nextY = doc.lastAutoTable.finalY + 8;
    }

    // Class list
    autoTable(doc, {
      startY: nextY,
      head: [['Data', 'Hora', 'Duração', 'Tipo', 'Modalidade', 'Valor Aula']],
      body: r.agendamentos.map(a => [
        a.data, a.hora_inicio, `${a.duracao} min`, a.tipo,
        a.modalidade === 'grupo' ? 'Grupo' : 'Particular',
        `R$ ${a.valor_aula.toFixed(2)}`,
      ]),
      theme: 'striped',
      styles: { fontSize: 9.5, halign: 'center', cellPadding: 4 },
      headStyles: { fillColor: purple, textColor: '#fff', fontStyle: 'bold' },
      columnStyles: { 5: { halign: 'right', fontStyle: 'bold' } },
      margin: { left: 14, right: 14 },
    });

    // Footer
    doc.setFontSize(8); doc.setTextColor(160, 160, 160);
    doc.text(`Gerado em ${new Date().toLocaleDateString('pt-BR')} — Aulas da Mema`, 105, doc.internal.pageSize.height - 8, { align: 'center' });

    doc.save(`relatorio_${r.aluno.replace(/ /g, '_')}_${r.periodo.replace(/[/ ]/g, '-')}.pdf`);
  }

  return (
    <div className="mema-screen" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-3)', paddingBottom: 90 }}>
      <ScreenHeader title="Relatório"/>

      {/* ── Filtros ── */}
      <div style={{ padding: '0 var(--gap-3)' }}>
        <Surface padded style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
          {tipo === 'professora' && (
            <FormField label="Aluno">
              <AlunoSearch alunos={alunos} value={alunoId} onChange={setAlunoId}/>
            </FormField>
          )}
          <FormField label="Período">
            <select value={modo} onChange={e => setModo(e.target.value)} style={selectStyle}>
              <option value="mes_atual">Mês atual</option>
              <option value="mes_passado">Mês passado</option>
              <option value="especifico">Mês/ano específico</option>
              <option value="personalizado">Personalizado</option>
            </select>
          </FormField>

          {modo === 'especifico' && (
            <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
              <FormField label="Mês" style={{ flex: 2 }}>
                <select value={mes} onChange={e => setMes(e.target.value)} style={selectStyle}>
                  <option value="">Selecione</option>
                  {MESES_FULL.map((m, i) => (
                    <option key={i + 1} value={String(i + 1).padStart(2, '0')}>{m}</option>
                  ))}
                </select>
              </FormField>
              <FormField label="Ano" style={{ flex: 1 }}>
                <input type="number" value={ano} onChange={e => setAno(e.target.value)}
                  min={2023} max={2100} style={inputCss}/>
              </FormField>
            </div>
          )}

          {modo === 'personalizado' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--gap-2)' }}>
              <FormField label="Início">
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)} style={dateInputStyle}/>
              </FormField>
              <FormField label="Fim">
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} style={dateInputStyle}/>
              </FormField>
            </div>
          )}
          {msg && <FeedbackMsg msg={msg}/>}
          <Button variant="primary" size="md" onClick={buscar} disabled={loading} style={{ width: '100%' }}>
            {loading ? 'Buscando…' : 'Gerar relatório'}
          </Button>
        </Surface>
      </div>

      {/* ── Resultado ── */}
      {relatorio && (
        <>
          {/* Cabeçalho do aluno */}
          <div style={{ padding: '0 var(--gap-3)' }}>
            <Surface padded>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--gap-2)', marginBottom: 'var(--gap-3)' }}>
                <Avatar name={relatorio.aluno} size={44}/>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: -0.3 }}>{relatorio.aluno}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginTop: 2 }}>
                    {relatorio.periodo} · {relatorio.resumo.aulas} aula{relatorio.resumo.aulas !== 1 ? 's' : ''} registrada{relatorio.resumo.aulas !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>

              {/* Badge mensal fixo */}
              {relatorio.mensal_fixo && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 8, marginBottom: 'var(--gap-2)',
                  padding: '10px 14px', borderRadius: 'var(--radius-md)',
                  background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 25%, transparent)',
                }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Mensal fixo
                  </span>
                  <span style={{ fontSize: 12, color: 'var(--accent)', opacity: 0.8 }}>
                    — valor calculado pelo contrato, não por hora
                  </span>
                </div>
              )}

              {/* Stats */}
              <div style={{ display: 'flex', gap: 'var(--gap-2)' }}>
                <StatCard label="Aulas" value={relatorio.resumo.aulas}/>
                <StatCard label="Horas" value={fmtHoras(relatorio.resumo.minutos)}/>
                <StatCard label="Total" value={fmtMoney(relatorio.resumo.valor_total)} accent/>
              </div>

              {/* Breakdown particular vs grupo */}
              {!relatorio.mensal_fixo && (relatorio.particular.aulas > 0 && relatorio.grupo.aulas > 0) && (
                <div style={{ display: 'flex', gap: 'var(--gap-2)', marginTop: 'var(--gap-2)' }}>
                  <ModalidadeCard
                    label="Particular" icon="user"
                    data={relatorio.particular}
                    colorBg="color-mix(in oklch, var(--accent) 8%, transparent)"
                    colorFg="var(--accent)"
                  />
                  <ModalidadeCard
                    label="Grupo" icon="users"
                    data={relatorio.grupo}
                    colorBg="color-mix(in oklch, var(--success) 8%, transparent)"
                    colorFg="var(--success)"
                  />
                </div>
              )}
            </Surface>
          </div>

          {/* Exportar */}
          <div style={{ padding: '0 var(--gap-3)', display: 'flex', gap: 'var(--gap-2)' }}>
            <Button variant="secondary" icon="download" size="sm" onClick={exportarCSV} style={{ flex: 1 }}>Exportar CSV</Button>
            <Button variant="secondary" icon="download" size="sm" onClick={exportarPDF} style={{ flex: 1 }}>Exportar PDF</Button>
          </div>

          {/* Lista de aulas */}
          <div style={{ padding: '0 var(--gap-3)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5,
              color: 'var(--text-tertiary)', marginBottom: 'var(--gap-2)' }}>
              Detalhamento
            </div>
            <Surface>
              {relatorio.agendamentos.length === 0 ? (
                <div style={{ padding: 'var(--gap-4)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 13 }}>
                  Nenhuma aula registrada neste período.
                </div>
              ) : relatorio.agendamentos.map((aula, i) => {
                const isGrupo = aula.modalidade === 'grupo';
                return (
                  <React.Fragment key={i}>
                    {i > 0 && <Divider/>}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px var(--pad-card)' }}>
                      {/* Linha colorida */}
                      <div style={{
                        width: 3, alignSelf: 'stretch', borderRadius: 4, flexShrink: 0,
                        background: isGrupo ? 'var(--success)' : 'var(--accent)',
                      }}/>

                      {/* Data + hora */}
                      <div style={{ minWidth: 54, flexShrink: 0 }}>
                        <div style={{ fontFamily: '"Geist Mono", monospace', fontSize: 12.5, fontWeight: 700,
                          color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }}>
                          {aula.hora_inicio}
                        </div>
                        <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 1 }}>{aula.data}</div>
                      </div>

                      {/* Info central */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                          <Badge tone={isGrupo ? 'success' : 'accent'}>
                            {isGrupo ? 'Grupo' : 'Particular'}
                          </Badge>
                          <Badge tone="neutral">
                            {aula.tipo || 'presencial'}
                          </Badge>
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 4 }}>
                          {fmtDur(aula.duracao)} · {fmtMoney(aula.valor_hora)}/h
                        </div>
                      </div>

                      {/* Valor da aula */}
                      <div style={{ fontFamily: '"Geist Mono", monospace', fontSize: 13.5, fontWeight: 700,
                        color: relatorio.mensal_fixo ? 'var(--text-tertiary)' : isGrupo ? 'var(--success)' : 'var(--accent)',
                        flexShrink: 0, textAlign: 'right' }}>
                        {relatorio.mensal_fixo ? '—' : fmtMoney(aula.valor_aula)}
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </Surface>
          </div>

          {/* Total final */}
          <div style={{ padding: '0 var(--gap-3)' }}>
            <Surface padded style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              background: 'var(--accent-soft)',
            }}>
              <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--accent)' }}>Total do período</span>
              <span style={{ fontFamily: '"Geist Mono", monospace', fontSize: 18, fontWeight: 700,
                color: 'var(--accent)', letterSpacing: -0.5 }}>
                {fmtMoney(relatorio.resumo.valor_total)}
              </span>
            </Surface>
          </div>
        </>
      )}
    </div>
  );
}
